// The hot bar (qa-runs#369, slice A; it grows out of the action bar of issue 1130, docs/VTT/design/29-the-attack-chain.md
// §2.1): a row of boxes docked along the board, from sidebar to sidebar, each holding one thing the character does most
// — an attack, a saved spell, a save, a skill or ability check, a plain throw — fired with one click or one key, as the
// action bar fired them. An attack enters the targeting step (`targetTool`: the next tap on a token sends
// `attack.request` naming both tokens and no number), or fires at once at the target this person has set; a spell lays
// its template (`areaTool.cast`, then `area.set` and `area.resolve`); a save or check throws the system's dice plus the
// number the server read from the sheet or stat block (`/checks`, as the dice bag of qa-runs#368 does); a throw is a
// plain `roll.request`. A box names its source by reference, never a number.
//
// Its presets are the token's loadouts (`object.loadouts`, sent to its owners and the DM alone: secrecy case 4ae), now
// with their boxes by position and their own hotkeys; "All" shows every attack and spell as the action bar did. Its
// look (size, opacity, boxes, rows, labels, top or bottom, the lock, the preset keys) is this person's, in their layout
// document (`LayoutDoc.panel.hotbar`, `panelPrefs`). A bar belongs to a character: a player's own token (a switcher
// when they have several), the DM's the token selected. Keys never fire while typing (issue 1422); Escape ends a step
// anywhere at the table but in a box (issue 1447).
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AREA_LIMITS, AREA_SHAPES, DICE_LIMITS, HOTBAR_LIMITS, HOT_LIMITS, ON_SAVE, SAVE_LIMITS, SPELL_LIMITS, damageText, isHotkey,
  parseDamageText, parseSaveLine, isHotIcon, PICTURE_LIMITS,
  knownMetamagic, type DiceBoost, type AreaShape, type AttackSpec, type HotBox, type ToggleChoice, type Loadout, type Loadouts, type OnSave, type SeenPool, type SlotLeft, type Spell, LONG_CAST, slotChoices, slotWords, spellAttackSpec, spellDamageAt,
  castKeysOf, castingKindOf, entryArea, entrySaveLine,
} from '@dragons-table/vtt-shared';
import { useTableState } from '../table/TableState';
import { MetamagicPicker, metaWire, type MetaChosen } from './MetamagicPicker';
import { useMayAct } from '../table/mayAct';
import { useFire, useTokenActions } from '../shell/panels/ActionParts';
import { ChosenRulings } from '../shell/panels/Rulings';
import { PendingAttack } from '../shell/panels/AttackParts';
import { useShell } from '../shell/context';
import { fetchChecks, type ChecksAnswer } from '../entries';
import { characterPageUrl, sheetWindowName } from '../characters';
import { useSheetPulse } from '../sheetPulse';
import { fetchSheetSpells, type SheetSpellRow, type SheetSpellsAnswer, type SheetTrait } from '../sheetSpells';
import { panelPrefs, useHotbarPrefs, type HotbarLook } from '../shell/panelPrefs';
import { selection } from './selection';
import { targetTool, type Targeting } from './targetTool';
import { areaTool, type AreaDraft } from './areaTool';
import { useTarget } from './target';
import { typingIn } from './typing';
import { BTN } from '../shell/ui';
import { pictureUrl, uploadBoxPicture } from '../assets';
import { HOT_ICON_LIST, HOT_ICON_WORDS, HotIconSvg } from './hotIcons';
import { carriesHot, readHotDrop } from './hotDrag';
import { useManeuver, useManeuversOf } from '../shell/panels/ManeuverParts';
import { specialState, type ManeuverView, type SpecialAction, type SpentSpecials } from '@dragons-table/vtt-shared';
import { ToggleLights, sendToggle } from './ToggleLights';
import { isLitNow, toggleDetail, toggleRef, useToggleChoices } from './toggleBoxes';

const btn = BTN;
const field = 'rounded bg-stone-800 px-1 py-0.5 text-xs';
const SHAPE_WORDS: Record<AreaShape, string> = { burst: 'Burst', cone: 'Cone', line: 'Line', rect: 'Rectangle' };
const ARROWS = ['→', '↘', '↓', '↙', '←', '↖', '↑', '↗'];
const NAMES = ['east', 'south-east', 'south', 'south-west', 'west', 'north-west', 'north', 'north-east'];
/** A 60-point "O" is about 44 px (Tom, 2026-10-28): the box's side for a size in points. */
export const boxPx = (size: number): number => Math.max(12, Math.round((size * 44) / 60));
const GAP = 4;

interface BarToken { id: string; kind: string; name?: string; values?: { feats?: string[] }; mine?: boolean; pc?: boolean; owners?: string[]; character?: string | null; entry?: unknown; spells?: Spell[]; loadouts?: Loadouts }
export type BarItem = { kind: 'attack'; key: string; label: string; spec: AttackSpec } | { kind: 'spell'; key: string; label: string; spell: Spell };

/** What a box can hold, read from the token's sources: its reference, its tab, its name and its numbers. */
export interface HotEntry { ref: string; tab: HotTab; label: string; detail: string; item?: BarItem; /** A spell of the sheet not yet on the token; `ready` when it needs nothing typed (see `spellOfRow`). */ row?: SheetSpellRow; ready?: boolean; /** A monster's ability the DM uses (`rules/specials.ts`). */ special?: SpecialAction; check?: { label: string; total: number }; maneuver?: ManeuverView; /** A Toggle rule (qa-runs#371): a tap switches it on or off; `lit` as the server last said. */ toggle?: ToggleChoice & { lit: boolean } }
export type HotTab = 'attack' | 'spell' | 'special' | 'save' | 'skill' | 'ability' | 'maneuver' | 'toggle' | 'dice';
const TABS: Array<{ tab: HotTab; label: string; find: string }> = [
  { tab: 'attack', label: 'Attacks', find: 'Search your attacks…' },
  { tab: 'spell', label: 'Spells', find: 'Search your spells…' },
  { tab: 'save', label: 'Saves', find: 'Search your saves…' },
  { tab: 'skill', label: 'Skills', find: 'Search your skills…' },
  { tab: 'ability', label: 'Ability checks', find: 'Search your abilities…' },
  { tab: 'special', label: 'Abilities', find: 'Search the abilities…' },
  { tab: 'maneuver', label: 'Maneuvers', find: 'Search the maneuvers…' },
  { tab: 'toggle', label: 'Toggles', find: 'Search your toggles…' },
  { tab: 'dice', label: 'Dice', find: '' },
];

const signed = (n: number) => (n >= 0 ? `+${n}` : `${n}`);
/** "+7 · 1d6+1 · 18–20/×2": an attack's bonus, damage and critical, as the hover shows them. */
export function attackDetail(spec: AttackSpec): string {
  const bonus = spec.terms.filter((t) => !t.aboutTarget).reduce((a, t) => a + t.value, 0);
  const parts = [signed(bonus)];
  if (spec.damage) parts.push(damageText(spec.damage).replace(/ ([+-]) /g, '$1'));
  if (spec.threat || spec.multiplier) parts.push(`${spec.threat && spec.threat < 20 ? `${spec.threat}–20` : '20'}/×${spec.multiplier ?? 2}`);
  return parts.join(' · ');
}
const DICE_RE = /^\s*([1-9][0-9]?)\s*d\s*([1-9][0-9]{0,2})\s*(?:([+-])\s*([0-9]{1,3}))?\s*$/i;
/** A typed throw ("2d6+3", "d20 - 1") as a box's reference, or null. */
export function diceRef(text: string): string | null {
  const m = DICE_RE.exec(text.replace(/^d/i, '1d'));
  if (!m) return null;
  const mod = m[4] && Number(m[4]) !== 0 ? `${m[3]}${Number(m[4])}` : '';
  return `dice:${Number(m[1])}d${Number(m[2])}${mod}`;
}

/**
 * The default hotkey of the box at a position: 1–9 and 0 for the first ten, then Alt+1… Alt+0 (slice B: Chrome keeps
 * Ctrl+digits for its tabs, so a page never sees them) or none, as the person set; none beyond twenty.
 */
export const defaultBoxKey = (i: number, past10: 'alt' | 'none' = 'alt'): string | null => (i < 10 ? String((i + 1) % 10) : i < 20 && past10 === 'alt' ? `Alt+${(i - 9) % 10}` : null);
/** The default hotkey of the preset at a position (All is 0): Shift+1… for the first nine saved ones. */
export const defaultPresetKey = (i: number): string | null => (i < 9 ? `Shift+${i + 1}` : null);
/** A key press as a hotkey's words: "Ctrl+Shift+2", "Tab", "Q". A digit is its key, whatever Shift makes it type. */
export function keyName(e: Pick<KeyboardEvent, 'key' | 'code' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'>): string | null {
  let k: string | null = null;
  if (/^Digit[0-9]$/.test(e.code)) k = e.code.slice(5);
  else if (/^Key[A-Z]$/.test(e.code)) k = e.code.slice(3);
  else if (e.key === 'Tab' || /^F[0-9]{1,2}$/.test(e.key)) k = e.key;
  else if (e.key.length === 1 && /[-=,./;'`[\]]/.test(e.key)) k = e.key;
  if (!k) return null;
  return `${e.ctrlKey || e.metaKey ? 'Ctrl+' : ''}${e.altKey ? 'Alt+' : ''}${e.shiftKey ? 'Shift+' : ''}${k}`;
}
const sameKey = (a: string | null | undefined, b: string | null) => !!a && !!b && a.toUpperCase() === b.toUpperCase();

function useSelected(): string | null {
  const read = () => { const ids = [...selection.get()]; return ids.length === 1 ? ids[0]! : null; };
  const [id, setId] = useState(read);
  useEffect(() => selection.subscribe(() => setId(read())), []);
  return id;
}
function useTool<T>(tool: { get: () => T; subscribe: (l: (v: T) => void) => () => void }): T {
  const [v, setV] = useState(tool.get());
  useEffect(() => tool.subscribe(setV), [tool]);
  return v;
}
function usePhone(): boolean {
  const q = '(max-width: 640px)';
  const [m, setM] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(q).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(q);
    if (!mq) return undefined;
    const on = () => setM(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return m;
}
function useWidth(ref: React.RefObject<HTMLElement | null>): number {
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setW(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

/** How this cast is paid for and what it reads of the sheet (docs/MRCS/dnd-5e.md §3.5). */
interface CastHow { slot?: string; slotLevel?: number | null; level?: number | null; attack?: number | null; ritual?: boolean; /** A power's augment points (qa-runs#1435); the server holds them to the manifester level. */ augment?: number }
const castWireOf = (how: CastHow | undefined) => ({ ...(how?.slot ? { slot: how.slot } : {}), ...(how?.ritual ? { ritual: true } : {}), ...(how?.augment ? { augment: how.augment } : {}) });
/** A power's base cost in power points (SRD): 2 x its level - 1; a 0-level power none. */
const powerBase = (level: number): number => (level > 0 ? 2 * level - 1 : 0);

/** The bar's items, attacks first then spells; with a loadout active, only its keys, in that order. */
export function barItems(attacks: BarItem[], spells: Spell[], active: { keys: string[] } | null): BarItem[] {
  const all: BarItem[] = [...attacks, ...spells.map((s): BarItem => ({ kind: 'spell', key: s.key, label: s.name, spell: s }))];
  return active ? all.filter((i) => active.keys.includes(i.key)) : all;
}
/** A preset's boxes: its own, or — one saved before the hot bar — its keys in order. */
export function presetBoxes(l: Loadout, entries: HotEntry[]): Array<HotBox | null> {
  if (l.boxes) return l.boxes;
  return l.keys.map((k) => entries.find((e) => e.item?.key === k)?.ref ?? null).filter((r): r is string => !!r).map((ref) => ({ ref }));
}
/** The attack and spell keys a preset's boxes hold, for what reads `keys`. */
const keysOf = (boxes: Array<HotBox | null>): string[] =>
  [...new Set(boxes.flatMap((b) => (b && /^(attack|spell):/.test(b.ref) ? [b.ref.slice(b.ref.indexOf(':') + 1)] : [])))].filter((k) => /^[a-z0-9][a-z0-9_-]{0,63}$/.test(k)).slice(0, SPELL_LIMITS.keys);
const trimBoxes = (boxes: Array<HotBox | null>): Array<HotBox | null> => {
  const out = boxes.slice(0, HOT_LIMITS.boxes);
  while (out.length && out[out.length - 1] === null) out.pop();
  return out;
};

/** The attacks a person chose to leave off a token's presets (qa-runs 1258), remembered on this device: a convenience, so a failed read just asks again. */
const leftOffKey = (tokenId: string) => `tdt.hotbar.leftOff.${tokenId}`;
const readLeftOff = (tokenId: string): string[] => {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(leftOffKey(tokenId)) ?? '[]');
    return Array.isArray(v) ? v.filter((r): r is string => typeof r === 'string') : [];
  } catch { return []; }
};
const writeLeftOff = (tokenId: string, refs: string[]) => {
  try { localStorage.setItem(leftOffKey(tokenId), JSON.stringify(refs)); } catch { /* it asks again next time */ }
};

/** Where the bar goes: Board renders one above and one under the board, and the person's setting picks. */
export function ActionBar({ where = 'bottom' }: { where?: 'top' | 'bottom' }) {
  const { store, role } = useTableState();
  const mayAct = useMayAct();
  const look = useHotbarPrefs();
  const id = useSelected();
  const [chosen, setChosen] = useState<string | null>(null);
  const objects = store.state.objects as Record<string, BarToken>;
  const isDm = mayAct('attack.request');
  const own = useMemo(() => Object.values(objects).filter((t) => t.kind === 'token' && t.mine === true), [objects]);
  if (look.position !== where) return null;
  const sel = id ? objects[id] : undefined;
  let o: BarToken | undefined;
  if (sel && sel.kind === 'token' && (isDm || sel.mine === true)) o = sel;
  else if (!isDm && role !== 'dm') o = (chosen ? own.find((t) => t.id === chosen) : undefined) ?? own.find((t) => t.pc) ?? own[0];
  if (!o) return null;
  return <Bar key={o.id} o={o} look={look} own={isDm ? [] : own} onChoose={(t) => { setChosen(t); if (sel && sel.id !== t) selection.only(t); }} />;
}

function Bar({ o, look, own, onChoose }: { o: BarToken; look: HotbarLook; own: BarToken[]; onChoose: (id: string) => void }) {
  const { act, meId, systemRules, setNotice, rulings, store, role, scene } = useTableState();
  const { campaignId, phone: phoneLayout = false } = useShell();
  const isDm = useMayAct()('area.resolve');
  const answer = useTokenActions(o, systemRules.attack !== null);
  const fire = useFire();
  // Combat maneuvers (qa-runs#398) as a box's kind: listed from the system's data, by the token's conditions.
  const maneuvers = useManeuversOf(o.id);
  const makeManeuver = useManeuver();
  // The target this person has set (issue 1423): a weapon fires at it in one click, as Properties' Actions do.
  const target = useTarget();
  const aimed = target && target !== o.id ? store.state.objects[target] as BarToken | undefined : undefined;
  const targeting = useTool<Targeting>(targetTool);
  const draft = useTool<AreaDraft>(areaTool);
  const phone = usePhone() || phoneLayout;
  // The phone layout's bar is its boxes alone (issue 1242): the presets and the bar's own settings behind ⋯, so the board keeps the height.
  const [barOpen, setBarOpen] = useState(false);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const width = useWidth(rowRef);
  const [picking, setPicking] = useState<string | null>(null);
  const [priv, setPriv] = useState(true);
  // The DM's rulings ticked for the bar's attacks (issue 1136), sent with each until unticked.
  const [ruled, setRuled] = useState<string[]>([]);
  const [writing, setWriting] = useState(false);
  const [settings, setSettings] = useState(false);
  const [editing, setEditing] = useState(false);
  const [assign, setAssign] = useState<number | null>(null);
  // The sheet's spells not yet on the token, read as a box is picked, and one the player still has to finish in New spell.
  const [sheetRows, setSheetRows] = useState<SheetSpellRow[]>([]);
  const [sheetRead, setSheetRead] = useState(false);
  const [traits, setTraits] = useState<SheetTrait[]>([]);
  // A sheet spell fired from a box: it goes on the token first, and is cast once the token holds it.
  const [firstCast, setFirstCast] = useState<string | null>(null);
  const [finishing, setFinishing] = useState<{ row: SheetSpellRow; box: number } | null>(null);
  const [naming, setNaming] = useState(false);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  // A filled box's right-click menu: where the person pressed, and which box.
  const [boxMenu, setBoxMenu] = useState<{ box: number; x: number; y: number } | null>(null);
  const [checks, setChecks] = useState<ChecksAnswer | 'asking'>('asking');
  // A preset chosen or saved here shows at once; the token's own value takes over when the table has it.
  const storedText = JSON.stringify(o.loadouts ?? null);
  const [local, setLocal] = useState<Loadouts | undefined>(undefined);
  useEffect(() => { setLocal(undefined); }, [storedText]);
  useEffect(() => {
    let live = true;
    if (systemRules.test) { setChecks(null); return () => { live = false; }; }
    setChecks('asking');
    void fetchChecks(campaignId, o.id, scene?.id ?? null).then((c) => { if (live) setChecks(c); });
    return () => { live = false; };
  }, [campaignId, o.id, scene?.id, systemRules.test]);
  // Saves and checks read again, quietly, when the link or the sheet changes (qa-runs 1257, 1258): a read made as the
  // link went on, before its numbers landed, stayed at 0 until a reload.
  const sheetPulseNow = useSheetPulse(o.id);
  const sheetNumbers = JSON.stringify((store.state.objects[o.id] as { values?: { sheet?: unknown } } | undefined)?.values?.sheet ?? null);
  const sheetStamp = `${sheetPulseNow} ${o.character ?? ''} ${sheetNumbers}`;
  const readStamp = useRef(sheetStamp);
  useEffect(() => {
    if (readStamp.current === sheetStamp || systemRules.test) return undefined;
    readStamp.current = sheetStamp;
    let live = true;
    void fetchChecks(campaignId, o.id, scene?.id ?? null).then((c) => { if (live) setChecks(c); });
    return () => { live = false; };
  }, [sheetStamp, campaignId, o.id, scene?.id, systemRules.test]);
  // A step that ended elsewhere (Escape, another tool) is no longer this box's.
  useEffect(() => { if (picking && !targeting.on && !(draft.on && draft.cast)) setPicking(null); }, [picking, targeting.on, draft.on, draft.cast]);
  useEffect(() => () => { targetTool.stop(); if (areaTool.get().cast) areaTool.stop(); }, []);
  // Escape ends the step wherever the focus is at the table (issue 1447). A key typed into a box stays the box's (issue 1422).
  useEffect(() => {
    if (!picking) return undefined;
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || typingIn(e.target)) return;
      targetTool.stop(); if (areaTool.get().cast) areaTool.stop(); setPicking(null);
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [picking]);

  const spells = o.spells ?? [];
  const mainDefenceKey = systemRules.attack?.main ?? '';
  const [choosing, setChoosing] = useState<(BarItem & { kind: 'spell' }) | null>(null);
  // A cast that spends by slot level (docs/MRCS/dnd-5e.md §3.5): the slots that will carry it, asked when there is a choice.
  // A power about to be manifested (qa-runs#1435): the augment points asked for, up to what the manifester level leaves.
  const [augmentAsk, setAugmentAsk] = useState<{ item: BarItem & { kind: 'spell' }; meta: MetaChosen | null | undefined; row: SheetSpellRow & { points: NonNullable<SheetSpellRow['points']> }; base: number; room: number; n: number } | null>(null);
  const [slotAsk, setSlotAsk] = useState<{ item: BarItem & { kind: 'spell' }; meta: MetaChosen | null | undefined; how: CastHow; choices: SlotLeft[] } | null>(null);
  const metaKnown = knownMetamagic(systemRules.metamagic, o.values?.feats);
  // The system's choice to cast defensively (`opportunity.casting.defensive`): the check, in place of the attack of opportunity.
  const [castDefensively, setCastDefensively] = useState(false);
  const [leftOff, setLeftOff] = useState<string[]>(() => readLeftOff(o.id));
  useEffect(() => { setLeftOff(readLeftOff(o.id)); }, [o.id]);
  const loadouts = local ?? o.loadouts ?? { list: [], active: null };
  const activeName = loadouts.active;
  const active = loadouts.list.find((l) => l.name === activeName) ?? null;
  const attacks: BarItem[] = answer && answer !== 'asking' ? answer.actions.map((a) => ({ kind: 'attack', key: a.key, label: a.label, spec: a.spec })) : [];
  const name = o.name ?? 'this token';
  // A monster's special abilities, the DM's alone: a breath or an aura as a template, the rest at a target or spent.
  const specials: SpecialAction[] = answer && answer !== 'asking' ? answer.specials ?? [] : [];
  const spent = (o as { spent?: SpentSpecials }).spent;
  const got = checks === 'asking' ? null : checks;
  // The token's Toggle rules (qa-runs#371), asked again when what it carries by name changes: its feats and conditions.
  const carried = (store.state.objects[o.id] as { values?: { feats?: unknown; conditions?: unknown } } | undefined)?.values;
  const toggleList = useToggleChoices(campaignId, o.id, JSON.stringify([carried?.feats ?? null, carried?.conditions ?? null]));
  const togglesNow = (store.state as { toggles?: unknown }).toggles;

  useEffect(() => {
    if (!o.character) { setSheetRows([]); setSheetRead(false); setTraits([]); return undefined; }
    let live = true;
    void fetchSheetSpells(campaignId, o.id, scene?.id ?? null).then((a) => { if (live) { setSheetRows(a?.rows ?? []); setTraits(a?.traits ?? []); setSheetRead(!!a && !a.notice); } });
    return () => { live = false; };
  }, [campaignId, o.id, o.character, scene?.id, sheetPulseNow]);

  // Everything a box can hold, from the token's own sources.
  const entries: HotEntry[] = useMemo(() => [
    ...attacks.map((i): HotEntry => ({ ref: `attack:${i.key}`, tab: 'attack', label: i.label, detail: i.kind === 'attack' ? attackDetail(i.spec) : '', item: i })),
    // A spell read from a sheet row that row has since left isn't the sheet's any more; a typed one stays.
    ...spells.filter((s) => !s.row || !sheetRead || sheetRows.some((r) => r.row === s.row)).map((s): HotEntry => ({ ref: `spell:${s.key}`, tab: 'spell', label: s.name, detail: spellWords(s, systemRules.distance?.perSquare ?? null), item: { kind: 'spell', key: s.key, label: s.name, spell: s } })),
    ...sheetRows.filter((r) => !spells.some((x) => x.row === r.row)).map((r): HotEntry => {
      const s = spellOfRow(r, systemRules);
      return { ref: `${SHEET_SPELL}${r.row}`, tab: 'spell', label: r.name, detail: s ? spellWords(s, systemRules.distance?.perSquare ?? null) || 'On the sheet' : 'On the sheet: its numbers are needed', row: r, ready: !!s && isReadyToday(r) };
    }),
    ...specials.map((a): HotEntry => ({ ref: `special:${a.key}`, tab: a.key.startsWith('spell_like') ? 'spell' : 'special', label: a.label, detail: a.detail, ...(a.spell && !a.spell.noArea ? { item: { kind: 'spell' as const, key: a.spell.key, label: a.label, spell: a.spell } } : { special: a }) })),
    // The sheet's traits and class features: read off, nothing rolled; a person puts one on a box if they want it.
    ...traits.map((t): HotEntry => { const key = `${TRAIT}${t.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`.slice(0, 64); return { ref: `special:${key}`, tab: 'special', label: t.name, detail: t.text.slice(0, 60), special: { key, label: t.name, detail: t.text } }; }),
    ...(specials.length && spent ? [{ ref: 'special:reset', tab: 'special' as const, label: 'A new day', detail: 'Its uses and recharges back', special: { key: 'reset', label: 'A new day', detail: '' } }] : []),
    ...(got?.dice ? got.saves.map((c): HotEntry => ({ ref: `save:${c.key}`, tab: 'save', label: `${c.label} save`, detail: signed(c.total), check: { label: `${c.label} save`, total: c.total } })) : []),
    ...(got?.dice ? got.skills.map((c): HotEntry => ({ ref: `skill:${c.key ?? c.name}`.slice(0, HOT_LIMITS.ref), tab: 'skill', label: c.name, detail: signed(c.total), check: { label: `${c.name} check`, total: c.total } })) : []),
    ...(got?.dice ? got.abilities.map((c): HotEntry => ({ ref: `ability:${c.key}`, tab: 'ability', label: `${c.label} check`, detail: signed(c.total), check: { label: `${c.label} check`, total: c.total } })) : []),
    ...maneuvers.map((m): HotEntry => ({ ref: `maneuver:${m.key}`, tab: 'maneuver', label: m.label, detail: m.needs.length ? 'In the grapple' : m.provokes ? 'Provokes' : '', maneuver: m })),
    ...toggleList.map((c): HotEntry => { const lit = isLitNow(store.state, o.id, c.id); return { ref: toggleRef(c.id), tab: 'toggle', label: c.name, detail: toggleDetail(c, lit), toggle: { ...c, lit } }; }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [answer, JSON.stringify(spells), JSON.stringify(sheetRows), JSON.stringify(traits), sheetRead, JSON.stringify(spent ?? null), got, JSON.stringify(maneuvers), toggleList, togglesNow]);
  const entryOf = (ref: string): HotEntry | null => {
    const e = entries.find((x) => x.ref === ref);
    if (e) return e;
    // A box filled from the sheet whose spell has since gone on the token.
    if (ref.startsWith(SHEET_SPELL)) { const s = spells.find((x) => x.row === ref.slice(SHEET_SPELL.length)); if (s) return entries.find((x) => x.ref === `spell:${s.key}`) ?? null; }
    if (ref.startsWith('dice:')) { const n = ref.slice(5); return { ref, tab: 'dice', label: n, detail: n }; }
    return null;
  };

  // The boxes of the preset showing: All is every attack and spell, in order, as the action bar listed them, the sheet's
  // spells among them, so a linked character's bar fills by itself. A preset is the person's own arrangement: never touched.
  const shown: Array<HotBox | null> = active ? presetBoxes(active, entries) : entries.filter((e) => (e.item && !e.ref.startsWith('special:spell_like')) || e.ready || e.toggle || (e.special && !e.special.perDay && !e.special.key.startsWith('spell_like') && !e.special.key.startsWith(TRAIT))).map((e) => ({ ref: e.ref }));
  const px = boxPx(look.size);
  const rows = phone ? 1 : look.rows;
  const perRow = Math.max(1, Math.floor((Math.max(width, px) + GAP) / (px + GAP)));
  const count = Math.min(HOT_LIMITS.boxes, Math.max(shown.length + (phone ? 1 : 0), look.count ?? (phone ? 10 : Math.max(10, perRow * rows))));
  const boxes: Array<HotBox | null> = Array.from({ length: count }, (_, i) => shown[i] ?? null);

  // The combatant's pools this person is sent (a player their own): a box whose key names one shows what is left.
  const enc = store.state.encounter as { combatants?: Array<{ tokens: string[]; pools?: Record<string, SeenPool> }> } | undefined;
  const pools = enc?.combatants?.find((c) => c.tokens.includes(o.id))?.pools;
  const round = (store.state.encounter as { round?: number } | undefined)?.round ?? 0;
  const specialNow = (ref: string) => { const a = ref.startsWith('special:') ? specials.find((x) => x.key === ref.slice(8)) : undefined; return a ? specialState(a, spent, round) : null; };
  const leftOf = (ref: string): number | null => {
    const sp = specialNow(ref);
    if (sp) return sp.wait > 0 || sp.recharging ? 0 : sp.left;
    const p = pools?.[ref.slice(ref.indexOf(':') + 1)];
    return p ? p.left : null;
  };

  const cancel = () => { targetTool.stop(); if (areaTool.get().cast) areaTool.stop(); setPicking(null); };
  const sendLoadouts = (next: Loadouts) => {
    setLocal(next);
    act({ kind: 'object.loadouts', id: o.id, loadouts: next }, (reply) => {
      if (!reply.ok) { setLocal(undefined); setNotice(reply.reason ?? 'Those presets could not be saved.'); }
    });
  };
  const choose = (n: string | null) => { cancel(); sendLoadouts({ list: loadouts.list, active: n }); };
  /** New boxes for the preset showing; on All, a new preset holding them ("Bar", "Bar 2"…) becomes the active one. */
  const saveBoxes = (next: Array<HotBox | null>) => {
    const b = trimBoxes(next);
    if (active) { sendLoadouts({ list: loadouts.list.map((l) => (l.name === active.name ? { ...l, boxes: b, keys: keysOf(b) } : l)), active: active.name }); return; }
    if (loadouts.list.length >= SPELL_LIMITS.loadouts) { setNotice(`At most ${SPELL_LIMITS.loadouts} presets: choose one to change.`); return; }
    let n = 'Bar'; for (let k = 2; loadouts.list.some((l) => l.name === n); k += 1) n = `Bar ${k}`;
    sendLoadouts({ list: [...loadouts.list, { name: n, keys: keysOf(b), boxes: b }], active: n });
  };
  const emptyBox = (i: number) => { const next = [...boxes]; next[i] = null; saveBoxes(next); setBoxMenu(null); setAssign(null); };
  const addPreset = (n: string) => {
    if (loadouts.list.length >= SPELL_LIMITS.loadouts) { setNotice(`At most ${SPELL_LIMITS.loadouts} presets.`); return; }
    if (loadouts.list.some((l) => l.name === n)) { choose(n); return; }
    sendLoadouts({ list: [...loadouts.list, { name: n, keys: [], boxes: [] }], active: n });
  };
  const setPresetKey = (hotkey: string | null) => {
    if (!active) return;
    sendLoadouts({ list: loadouts.list.map((l) => { if (l.name !== active.name) return l; const { hotkey: _h, ...rest } = l; return hotkey ? { ...rest, hotkey } : rest; }), active: active.name });
  };
  const removePreset = () => { if (active) sendLoadouts({ list: loadouts.list.filter((l) => l.name !== active.name), active: null }); };

  const castAt = (spell: Spell, origin: { i: number; j: number }, d: AreaDraft, meta?: MetaChosen, how?: CastHow) => {
    const dist = systemRules.distance;
    if (!dist) return;
    const areaId = crypto.randomUUID();
    // Its maker (qa-runs#1451): a cone or a line never catches the token casting it.
    const area = { id: areaId, ownerId: meId, maker: o.id, shape: spell.shape, origin, size: meta?.cast.size ?? spell.size, size2: spell.shape === 'rect' ? meta?.cast.size2 ?? spell.size2 ?? spell.size : meta?.cast.size ?? spell.size, diagonal: dist.diagonal, includes: systemRules.areaIncludes, dir: d.dir, label: (meta?.cast.name ?? spell.name).slice(0, AREA_LIMITS.label) };
    // The token casting and its spell (qa-runs 408): the server spends the action the spell's casting time names.
    act({ kind: 'area.set', area, cast: { caster: o.id, spell: spell.key, ...metaWire(meta, isDm && priv), ...castWireOf(how), ...(castDefensively ? { defensive: true } : {}) } } as never, (reply) => {
      if (!reply.ok) { setNotice(reply.reason ?? 'That template could not be laid.'); return; }
      if (reply.notice) setNotice(reply.notice);
      const save = spell.saveLine ? parseSaveLine(spell.saveLine, systemRules.saves) : null;
      const dc = meta ? meta.cast.dc : spell.dc;
      if (!save || dc === null || systemRules.areaIncludes === null) return; // no save it can roll: the template alone
      // A line that leaves a success to the DM ("Fortitude partial", issue 1448) rolls with what the spell's author chose.
      const onSave = save.leftToDm ? spell.onSave : save.onSave;
      if (!onSave) { setNotice(`${spell.name}: its save line leaves what a success does to you, so its saves were not rolled. Resolve them from its area.`); return; }
      const damage = spellDamageAt(spell, { slot: how?.slotLevel ?? null, level: how?.level ?? null });
      act({ kind: 'area.resolve', areaId, save: save.save, onSave, dc, ...(damage ? { damage, ...(meta?.cast.boost ? { boost: meta.cast.boost } : {}) } : {}), private: isDm ? priv : false, name: (meta?.cast.name ?? spell.name).slice(0, SAVE_LIMITS.name), caster: o.id, against: ['spell', ...(spell.descriptors ?? [])] } as never, (r) => {
        if (!r.ok) setNotice(r.reason ?? 'Those saves could not be rolled.');
      });
    });
  };

  /**
   * A spell with no area (qa-runs 1180: touch, one target): cast at once, no template. The server spends its sheet slot
   * as a template's cast does (`object.spells` naming it in `cast`); its damage, if any, is thrown as a plain roll.
   */
  const castNoArea = (spell: Spell, meta?: MetaChosen, how?: CastHow, at?: string) => {
    // The token aimed at (qa-runs#1442): a buff's rule takes it as its target rather than asking in the roll log.
    const onto = at ?? (aimed?.kind === 'token' ? aimed.id : undefined);
    act({ kind: 'object.spells', id: o.id, spells, cast: spell.key, ...(onto ? { at: onto } : {}), ...metaWire(meta, isDm && priv), ...castWireOf(how), ...(castDefensively ? { castDefensive: true } : {}) } as never, (reply) => {
      if (!reply.ok) { setNotice(reply.reason ?? `${spell.name} could not be cast.`); return; }
      const dc = meta ? meta.cast.dc : spell.dc;
      const save = spell.saveLine && dc !== null ? ` ${spell.saveLine}, DC ${dc}: the target rolls it.` : '';
      setNotice(reply.notice ?? `${spell.name}: cast.${save}`);
      const dmg = spellDamageAt(spell, { slot: how?.slotLevel ?? null, level: how?.level ?? null });
      // An attack (`Spell.attack`): the attack flow, with the sheet's bonus. The cast took its action.
      if (spell.attack && at) { fire(o.id, at, spellAttackSpec(spell, how?.attack ?? null, mainDefenceKey, dmg), priv, [], true); return; }
      const first = dmg?.parts[0];
      // The type it was written with rides in the label; Apply starts on it.
      if (dmg && first) throwDice(`${name}: ${meta?.cast.name ?? spell.name}${first.label ? ` (${first.label})` : ''}`, first.count, first.sides, dmg.bonus, meta?.cast.boost);
    });
  };

  /** An ability at a target, or one spent or read off: the server reads it from the stat block. */
  const useSpecial = (a: SpecialAction) => {
    const send = (target: string | null) => act({ kind: 'special.use', token: o.id, ...(a.key === 'reset' ? { reset: true } : { key: a.key }), ...(target ? { target } : {}) } as never, (reply) => {
      setNotice(reply.ok ? `${a.label}${a.detail ? `: ${a.detail}` : ''}` : reply.reason ?? `${a.label} could not be used.`);
    });
    if (a.key === 'pounce' || a.key.startsWith(TRAIT)) { setNotice(`${a.label}${a.detail ? `: ${a.detail}` : ''}`); return; }
    if (!a.target) { send(null); return; }
    if (aimed?.kind === 'token') { send(aimed.id); return; }
    targetTool.start(a.label, (t) => {
      if (t === o.id) { setNotice(`${name} cannot use that on itself.`); return; }
      send(t);
    });
  };

  /** A plain throw for this token, as the dice bag sends one: the DM's private unless unticked. */
  const throwDice = (label: string, count: number, sides: number, modifier: number, boost?: DiceBoost) => {
    act({ kind: 'roll.request', die: { kind: 'd', sides }, quantity: count, modifier, label: label.slice(0, DICE_LIMITS.label), private: isDm ? priv : false, forUserId: null, ...(role === 'player' ? { token: o.id } : {}), ...(boost ? { boost } : {}) } as never, (reply) => {
      if (!reply.ok) setNotice(reply.reason ?? 'That roll could not be made.');
    });
  };

  /** What a cast reads of the sheet, then the slot it spends: asked when there is a choice, or the ritual is. */
  const prepare = (item: BarItem & { kind: 'spell' }, meta?: MetaChosen | null) => {
    void fetchSheetSpells(campaignId, o.id, scene?.id ?? null).then((a) => {
      const c = a?.casting;
      const how: CastHow = { level: c?.level ?? null, attack: c?.attack ?? null };
      const own = item.spell.level ?? 0;
      const spends = systemRules.casting?.slots === 'by_level' && own > 0;
      const choices = spends ? slotChoices(c?.slots ?? [], own) : [];
      const ritual = systemRules.casting?.rituals === true && item.spell.ritual === true;
      if (choices.length > 1 || ritual) { setSlotAsk({ item, meta, how, choices }); return; }
      pick(item, meta, { ...how, ...(choices[0] ? { slot: choices[0].which, slotLevel: choices[0].level } : {}) });
    });
  };

  const pick = (item: BarItem, meta?: MetaChosen | null, how?: CastHow) => {
    setChoosing(null);
    setSlotAsk(null);
    setAugmentAsk(null);
    if (picking === item.key) { cancel(); return; }
    cancel();
    // A sheet spell, and the sheet lists metamagic (qa-runs#366): the choices come first.
    if (!meta && meta !== null && item.kind === 'spell' && item.spell.row && metaKnown.length) { setChoosing(item); return; }
    // A power of the sheet (qa-runs#1435): manifested from the power point reserve, with augment points asked first.
    const power = item.kind === 'spell' && !how && item.spell.row ? sheetRows.find((r) => r.row === item.spell.row && r.points && r.level !== undefined) : undefined;
    if (item.kind === 'spell' && power?.points && power.level !== undefined) {
      const base = powerBase(power.level);
      const room = Math.max(0, power.points.ml - base);
      if (room > 0) { setAugmentAsk({ item, meta, row: { ...power, points: power.points }, base, room, n: 0 }); return; }
      how = { level: power.points.ml, attack: null };
    }
    if (item.kind === 'spell' && !how && item.spell.row && systemRules.casting) { prepare(item, meta); return; }
    if (item.kind === 'spell' && item.spell.noArea && item.spell.attack) {
      const s = item.spell;
      if (aimed?.kind === 'token') { castNoArea(s, meta ?? undefined, how, aimed.id); return; }
      setPicking(item.key);
      targetTool.start(item.label, (t) => { setPicking(null); if (t === o.id) { setNotice(`${name} cannot attack itself.`); return; } castNoArea(s, meta ?? undefined, how, t); });
      return;
    }
    if (item.kind === 'spell' && item.spell.noArea) { castNoArea(item.spell, meta ?? undefined, how); return; }
    if (item.kind === 'attack' && aimed?.kind === 'token') { fire(o.id, aimed.id, item.spec, priv, ruled.filter((id) => (rulings ?? []).some((r) => r.id === id))); return; }
    setPicking(item.key);
    if (item.kind === 'attack') {
      targetTool.start(item.label, (t) => {
        setPicking(null);
        if (t === o.id) { setNotice(`${name} cannot attack itself.`); return; }
        fire(o.id, t, item.spec, priv, ruled.filter((id) => (rulings ?? []).some((r) => r.id === id)));
      });
    } else {
      const s = item.spell;
      const size = meta?.cast.size ?? s.size;
      areaTool.cast({ shape: s.shape, size, size2: s.shape === 'rect' ? meta?.cast.size2 ?? s.size2 ?? s.size : size, label: meta?.cast.name ?? s.name }, (origin, d) => { setPicking(null); castAt(s, origin, d, meta ?? undefined, how); });
    }
  };
  /** A sheet spell fired from a box: on the token, then cast (`firstCast`). One that needs numbers goes to New spell. */
  const fromSheet = (r: SheetSpellRow, box: number) => {
    const spell = spellOfRow(r, systemRules);
    if (!spell) { setFinishing({ row: r, box }); setWriting(true); setSettings(false); return; }
    act({ kind: 'object.spells', id: o.id, spells: [...spells, spell] }, (reply) => {
      if (reply.ok) setFirstCast(spell.key); else setNotice(reply.reason ?? 'That spell could not be saved.');
    });
  };
  useEffect(() => {
    const e = firstCast ? entries.find((x) => x.item?.key === firstCast) : undefined;
    if (e?.item) { setFirstCast(null); pick(e.item); }
  }, [firstCast, entries]); // eslint-disable-line react-hooks/exhaustive-deps
  /** A box clicked or its key pressed: what the action bar did for an attack or spell; a check or throw rolls at once. */
  const fireBox = (i: number) => {
    const b = boxes[i];
    if (!b) { setAssign(i); return; }
    const e = entryOf(b.ref);
    if (!e) { setNotice(`${b.label ?? b.ref}: ${name}'s sheet no longer has it.`); return; }
    const wait = specialNow(b.ref)?.wait ?? 0;
    if (leftOf(b.ref) === 0) { setNotice(`${b.label ?? e.label}: ${specialNow(b.ref)?.recharging ? 'recharging' : wait ? `ready in ${wait} round${wait === 1 ? '' : 's'}` : 'none left'}.`); return; }
    if (e.special) { cancel(); useSpecial(e.special); return; }
    if (e.row) { fromSheet(e.row, i); return; }
    if (e.item) { if (e.item.kind === 'spell' && !e.item.spell.noArea && !systemRules.distance) return; pick(e.item); return; }
    if (e.maneuver) { cancel(); makeManeuver(o.id, name, e.maneuver, aimed?.kind === 'token' ? aimed.id : null); return; }
    // A Toggle (qa-runs#371): on if off, off if on. The server spends its action on the token's own turn, or refuses.
    if (e.toggle) { const t = e.toggle; sendToggle(act as never, o.id, t.id, !t.lit, (err) => { if (err) setNotice(`${t.name}: ${err}`); }); return; }
    if (e.check && got?.dice) { throwDice(`${name}: ${e.check.label}`, got.dice.count, got.dice.sides, e.check.total); return; }
    if (e.tab === 'dice') { const m = /^(\d+)d(\d+)([+-]\d+)?$/.exec(e.label); if (m) throwDice(`${name}: ${b.label ?? e.label}`, Number(m[1]), Number(m[2]), Number(m[3] ?? 0)); }
  };
  const keyOf = (i: number): string | null => boxes[i]?.hotkey ?? defaultBoxKey(i, look.past10);

  // The bar's keys: a box's, a preset's, next and previous. Never while typing, never a held key's repeats; Tab only
  // when nothing on the page has the focus (or the board does), so it still moves between controls.
  const keysRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keysRef.current = (e: KeyboardEvent) => {
    if (e.defaultPrevented || e.repeat || typingIn(e.target)) return;
    const k = keyName(e);
    if (!k) return;
    if (k === 'Tab' || k === 'Shift+Tab') {
      const a = document.activeElement;
      if (a && a !== document.body && !a.closest?.('[data-testid="board-area"]')) return;
    }
    const names = [null, ...loadouts.list.map((l) => l.name)];
    const at = names.indexOf(activeName);
    if (sameKey(look.next, k) || sameKey(look.prev, k)) {
      if (names.length < 2) return;
      e.preventDefault();
      choose(names[(at + (sameKey(look.next, k) ? 1 : names.length - 1)) % names.length]!);
      return;
    }
    const preset = loadouts.list.findIndex((l, n) => sameKey(l.hotkey ?? defaultPresetKey(n), k));
    if (preset >= 0) { e.preventDefault(); choose(loadouts.list[preset]!.name); return; }
    for (let i = 0; i < boxes.length; i += 1) if (boxes[i] && sameKey(keyOf(i), k)) { e.preventDefault(); fireBox(i); return; }
  };
  useEffect(() => {
    const on = (e: KeyboardEvent) => keysRef.current(e);
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, []);

  /**
   * An entry dragged in from the sheet, the dice bag or Properties' Actions (slice B) fills box `i`, keeping its key and
   * picture. An entry of another token's sheet is refused: this bar fires this token's. A stat block's attack named by
   * another key is found by its name.
   */
  const dropIn = (i: number, ev: React.DragEvent) => {
    const d = readHotDrop(ev);
    if (!d) return;
    if (d.token && d.token !== o.id && !d.ref.startsWith('dice:')) { setNotice(`${d.label} is another token's: this bar is ${name}'s.`); return; }
    const tab = d.ref.slice(0, d.ref.indexOf(':'));
    const ref = entryOf(d.ref) ? d.ref : entries.find((e) => e.tab === tab && e.label === d.label)?.ref ?? d.ref;
    const old = boxes[i];
    const next = [...boxes];
    next[i] = { ref, ...(entryOf(ref) ? {} : { label: d.label.slice(0, HOT_LIMITS.label) }), ...(old?.hotkey ? { hotkey: old.hotkey } : {}), ...(old?.icon ? { icon: old.icon } : {}), ...(old?.picture ? { picture: old.picture } : {}) };
    saveBoxes(next);
  };
  const move = (from: number, to: number) => {
    if (from === to || to < 0 || to >= boxes.length) return;
    const next = [...boxes];
    const [b] = next.splice(from, 1);
    next.splice(to, 0, b ?? null);
    saveBoxes(next);
  };
  const hover = (i: number): string => {
    const b = boxes[i];
    const key = keyOf(i);
    if (!b) return `Empty box: click to fill it${key ? ` · key ${key}` : ''}`;
    const e = entryOf(b.ref);
    const aim = e?.item?.kind === 'attack' && aimed?.kind === 'token' ? ` at ${aimed.name ?? 'the target'}` : '';
    return [`${b.label ?? e?.label ?? b.ref}${aim}`, e?.detail, specialNow(b.ref)?.recharging ? 'recharging' : specialNow(b.ref)?.wait ? `ready in ${specialNow(b.ref)!.wait} rounds` : leftOf(b.ref) !== null ? `${leftOf(b.ref)} left` : '', key ? `key ${key}` : ''].filter(Boolean).join(' · ');
  };
  // A bar with nothing on it says why, and what to do next (qa-runs 1252).
  const nothingYet = answer !== 'asking' && checks !== 'asking' && !attacks.length && !spells.length && !specials.length
    && !(got?.dice && (got.saves.length || got.skills.length || got.abilities.length));
  const noLink = isDm ? "This token isn't linked to anything, so its bar is empty. Link it in Properties under Match to a table character." : "This token isn't linked to your character, so its bar is empty. Ask your DM to link it.";
  // A weapon on the sheet that can't be rolled yet, or a sheet that can't be read: the server's words.
  const attackNote = answer && answer !== 'asking' && answer.from === 'sheet' && !attacks.length ? answer.notice ?? null : null;
  let emptyWhy: { text: string; sheet?: string } | null = null;
  if (nothingYet) {
    if (!o.character && !o.entry) emptyWhy = { text: noLink };
    else if (o.character && !o.mine && !got) emptyWhy = { text: "This is a player's sheet, so its numbers show on their screen, not here. It opens from Properties." };
    else if (o.character) emptyWhy = { text: "The sheet has no ability scores yet, so there's nothing to roll.", ...(o.mine ? { sheet: o.character } : {}) };
  }
  if (!emptyWhy && attackNote && !spells.length) emptyWhy = { text: attackNote, ...(o.mine && o.character ? { sheet: o.character } : {}) };
  // A preset is the person's own and is never touched, so a weapon the sheet gained since doesn't land on it by itself
  // (qa-runs 1258): the bar says so and offers it, once a person has an arrangement to keep.
  const offPreset = active ? entries.filter((e) => e.tab === 'attack' && e.item && !boxes.some((b) => b?.ref === e.ref) && !leftOff.includes(e.ref)) : [];
  const addToPreset = () => {
    const next = [...boxes];
    for (const e of offPreset) {
      const free = next.indexOf(null);
      if (free >= 0) next[free] = { ref: e.ref }; else next.push({ ref: e.ref });
    }
    saveBoxes(next);
  };
  const leaveOff = () => { const refs = [...leftOff, ...offPreset.map((e) => e.ref)]; setLeftOff(refs); writeLeftOff(o.id, refs); };
  /** Why one of the picker's tabs is empty, when there is more to say than that it is (qa-runs 1252). */
  const tabWhy = (t: HotTab): string | null => {
    if (!o.character && !o.entry) return noLink;
    if (t === 'attack') return attackNote;
    if ((t === 'save' || t === 'skill' || t === 'ability') && o.character && got) return got.notice ?? 'The sheet has no numbers for these yet. Finish it, and they show here.';
    return null;
  };
  const shortOf = (s: string) => s.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?';
  const pickedEntry = picking ? entries.find((e) => e.item?.key === picking) : undefined;
  const assigned = assign !== null ? boxes[assign] ?? null : null;
  const font = Math.max(9, Math.round(px * 0.3));

  return (
    <section className="flex shrink-0 flex-col gap-1 border-stone-800 bg-stone-950 px-2 py-1 text-xs text-stone-200" style={{ opacity: look.opacity / 100, borderTopWidth: look.position === 'bottom' ? 1 : 0, borderBottomWidth: look.position === 'top' ? 1 : 0 }}
      data-testid="action-bar" aria-label={`Hot bar of ${name}`}>
      <div className={phoneLayout && !barOpen ? 'hidden' : 'flex flex-wrap items-center gap-1'} role="group" aria-label="Presets">
        {own.length > 1 ? (
          <label className="flex items-center gap-1 text-amber-300">for
            <select aria-label="Whose bar" className={field} value={o.id} onChange={(e) => onChoose(e.target.value)}>
              {own.map((t) => <option key={t.id} value={t.id}>{t.name ?? 'Unnamed token'}</option>)}
            </select>
          </label>
        ) : <span className="font-semibold text-amber-300">{name}</span>}
        <div role="tablist" aria-label="Presets" className="flex flex-wrap items-center gap-1">
          <button type="button" role="tab" className={`${btn} ${activeName === null ? 'border-amber-500 text-amber-300' : ''}`} aria-selected={activeName === null} aria-pressed={activeName === null} title={`Every attack and spell · next ${look.next}, previous ${look.prev}`} onClick={() => choose(null)}>All</button>
          {loadouts.list.map((l, n) => {
            const key = l.hotkey ?? defaultPresetKey(n);
            return <button key={l.name} type="button" role="tab" className={`${btn} ${activeName === l.name ? 'border-amber-500 text-amber-300' : ''}`} aria-selected={activeName === l.name} aria-pressed={activeName === l.name} title={key ? `${l.name} · key ${key}` : l.name} onClick={() => choose(l.name)}>{l.name}</button>;
          })}
          {naming ? <PresetName onSave={(n) => { addPreset(n); setNaming(false); }} onClose={() => setNaming(false)} /> : <button type="button" className={btn} onClick={() => setNaming(true)}>+ Preset</button>}
        </div>
        <span className="ml-auto flex items-center gap-1">
          {isDm && <label className="flex items-center gap-1"><input type="checkbox" aria-label="Private" checked={priv} onChange={(e) => setPriv(e.target.checked)} />Private</label>}
          <ChosenRulings to="attack" chosen={ruled} onChange={setRuled} />
          {/* A player casts only what their sheet holds (qa-runs#1398): homebrew reaches a sheet as a record. The DM types one for any token, a monster's beside its stat block (qa-runs#1397). */}
          {isDm && <button type="button" className={btn} aria-expanded={writing} title="Type a spell for this token, beside what its stat block or sheet holds (a Fireball for a goblin)" onClick={() => { setWriting((w) => !w); setSettings(false); }}>New spell…</button>}
          <button type="button" className={`${btn} ${editing ? 'border-amber-500 text-amber-300' : ''}`} aria-pressed={editing} title="Click a box to change its contents, label or key; × empties it" onClick={() => setEditing((v) => !v)}>Edit boxes</button>
          <button type="button" className={btn} aria-pressed={look.locked} aria-label={look.locked ? 'Unlock the boxes' : 'Lock the boxes'} title={look.locked ? 'Locked: boxes cannot be dragged' : 'Unlocked: drag a box to move it'} onClick={() => panelPrefs.setHotbar({ locked: !look.locked })}>{look.locked ? '🔒' : '🔓'}</button>
          <button type="button" className={btn} aria-expanded={settings} aria-label="Hot bar settings" onClick={() => { setSettings((v) => !v); setWriting(false); }}>⚙</button>
        </span>
      </div>
      {answer === 'asking' && <p className="text-stone-500">Reading its actions…</p>}
      <div className={phoneLayout ? 'flex items-center gap-1' : 'contents'}>
      {phoneLayout && <button type="button" className={`${btn} shrink-0`} aria-expanded={barOpen} aria-label={barOpen ? 'Hide presets and settings' : 'Presets and settings'} data-testid="action-bar-more" onClick={() => setBarOpen((v) => !v)}>⋯</button>}
      <div ref={rowRef} role="toolbar" aria-label="Actions" data-testid="action-bar-items"
        className={phone ? `flex flex-nowrap overflow-x-auto ${phoneLayout ? 'min-w-0 flex-1' : ''}` : 'flex flex-wrap'} style={{ gap: GAP, ...(phone ? {} : { maxHeight: rows * (px + GAP) + (look.labels === 'always' ? rows * 14 : 0), overflowX: 'hidden', overflowY: 'auto' }) }}>
        {boxes.map((b, i) => {
          const e = b ? entryOf(b.ref) : null;
          const left = b ? leftOf(b.ref) : null;
          const spent = left === 0;
          const wait = b ? specialNow(b.ref)?.wait ?? 0 : 0;
          const recharging = b ? specialNow(b.ref)?.recharging === true : false;
          const label = b ? b.label ?? e?.label ?? (answer === 'asking' ? '…' : 'Gone') : '';
          const key = keyOf(i);
          const kind = e?.tab ?? (b ? 'gone' : 'empty');
          const lit = !!e?.toggle?.lit;
          return (
            <div key={i} className="relative flex shrink-0 flex-col items-center" style={{ width: px }}>
              <button type="button" data-kind={kind === 'attack' || kind === 'spell' ? kind : `hot-${kind}`} data-testid={`hot-box-${i}`}
                aria-label={b ? `${label}${e?.toggle ? (lit ? ', on' : ', off') : ''}${key ? `, key ${key}` : ''}${recharging ? ', recharging' : wait ? `, ready in ${wait} round${wait === 1 ? '' : 's'}` : spent ? ', 0 left' : ''}` : `Empty box ${i + 1}: fill it`}
                aria-pressed={e?.toggle ? lit : !!e?.item && picking === e.item.key} data-lit={e?.toggle ? String(lit) : undefined} title={hover(i)}
                draggable={!look.locked && !phone && !!b} onDragStart={(ev) => { setDragFrom(i); ev.dataTransfer?.setData('text/plain', String(i)); }}
                onDragOver={(ev) => { if (dragFrom !== null || carriesHot(ev)) { ev.preventDefault(); if (ev.dataTransfer && dragFrom === null) ev.dataTransfer.dropEffect = 'copy'; } }}
                onDrop={(ev) => { ev.preventDefault(); if (dragFrom !== null) move(dragFrom, i); else dropIn(i, ev); setDragFrom(null); }} onDragEnd={() => setDragFrom(null)}
                disabled={!editing && !!e?.item && e.item.kind === 'spell' && !e.item.spell.noArea && !systemRules.distance}
                className={`relative flex items-center justify-center overflow-hidden rounded border text-center leading-none ${!b ? 'border-dashed border-stone-700 text-stone-500 hover:border-stone-500' : kind === 'attack' ? 'border-amber-700' : kind === 'spell' ? 'border-sky-700' : kind === 'toggle' ? (lit ? 'border-amber-400 shadow-[0_0_6px_#f59e0b]' : 'border-violet-700') : kind === 'gone' ? 'border-rose-800 text-stone-500' : 'border-emerald-800'} ${spent ? 'opacity-40 grayscale' : ''} ${(e?.item && picking === e.item.key) || lit ? 'bg-amber-900' : 'bg-stone-900 hover:bg-stone-800'}`}
                style={{ width: px, height: px, fontSize: font }}
                onClick={() => (editing ? setAssign(i) : fireBox(i))} onContextMenu={(ev) => { if (!b) return; ev.preventDefault(); setBoxMenu({ box: i, x: ev.clientX, y: ev.clientY }); }}>
                {b ? <BoxFace box={b} short={shortOf(label)} px={px} /> : <span aria-hidden="true">+</span>}
                {key && px >= 24 && <span aria-hidden="true" className="absolute left-0.5 top-0 text-stone-400" style={{ fontSize: Math.max(8, Math.round(px * 0.2)) }}>{key.replace('Ctrl+', '^').replace('Alt+', '⌥')}</span>}
                {spent && <span className="absolute inset-x-0 bottom-0 bg-stone-950/80 text-rose-300" style={{ fontSize: Math.max(8, Math.round(px * 0.2)) }}>{recharging ? 'recharging' : wait ? `${wait} rd` : '0 left'}</span>}
                {!spent && left !== null && px >= 24 && <span aria-hidden="true" className="absolute bottom-0 right-0.5 text-stone-300" style={{ fontSize: Math.max(8, Math.round(px * 0.2)) }}>{left}</span>}
              </button>
              {editing && b && <button type="button" data-testid={`hot-empty-${i}`} aria-label={`Empty box ${i + 1}, ${label}`} title="Empty this box" className="absolute right-0 top-0 z-10 flex h-4 w-4 items-center justify-center rounded-bl rounded-tr border border-stone-600 bg-stone-950 text-[0.7rem] leading-none text-rose-300 hover:border-rose-500" onClick={() => emptyBox(i)}>×</button>}
              {look.labels === 'always' && b && <span className="w-full truncate text-center text-[0.625rem] text-stone-400" aria-hidden="true">{label}</span>}
            </div>
          );
        })}
      </div>
      </div>
      {boxMenu && boxes[boxMenu.box] && (
        <>
          <button type="button" aria-label="Close the menu" className="fixed inset-0 z-40 cursor-default" onClick={() => setBoxMenu(null)} onContextMenu={(ev) => { ev.preventDefault(); setBoxMenu(null); }} />
          <div role="menu" aria-label={`Box ${boxMenu.box + 1}`} data-testid="hot-box-menu" className="fixed z-50 flex flex-col gap-0.5 rounded border border-stone-700 bg-stone-950 p-1" style={{ left: boxMenu.x, top: Math.max(4, boxMenu.y - 64) }}
            onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setBoxMenu(null); } }}>
            <button type="button" role="menuitem" autoFocus className={btn} onClick={() => emptyBox(boxMenu.box)}>Empty this box</button>
            <button type="button" role="menuitem" className={btn} onClick={() => { setAssign(boxMenu.box); setBoxMenu(null); }}>Change this box…</button>
          </div>
        </>
      )}
      <ToggleLights token={o.id} mayAct />
      {editing && active && (
        <p className="flex flex-wrap items-center gap-1 text-stone-400">
          <HotkeyInput label={`Key for the preset ${active.name}`} value={active.hotkey ?? defaultPresetKey(loadouts.list.indexOf(active)) ?? ''} onSave={setPresetKey} />
          <button type="button" className={btn} onClick={removePreset}>Delete the preset {active.name}</button>
        </p>
      )}
      {pickedEntry?.item && (
        <p className="flex flex-wrap items-center gap-1 text-amber-200" role="status" data-testid="action-bar-step">
          {pickedEntry.item.kind === 'attack' ? `Tap the token ${name} attacks with ${pickedEntry.item.label}.` : `Tap the corner between squares where ${pickedEntry.item.label} goes.`}
          {pickedEntry.item.kind === 'spell' && pickedEntry.item.spell.shape !== 'burst' && (
            <span role="radiogroup" aria-label="Direction" className="flex gap-0.5">
              {ARROWS.map((a, k) => (
                <button key={a} type="button" role="radio" aria-checked={draft.dir === k} aria-label={NAMES[k]} className={`${btn} ${draft.dir === k ? 'border-amber-500 text-amber-300' : ''}`} onClick={() => areaTool.change({ dir: k })}>{a}</button>
              ))}
            </span>
          )}
          <button type="button" className={btn} onClick={cancel}>Cancel</button>
        </p>
      )}
      {systemRules.castDefensive && (o.spells?.length ?? 0) > 0 && (
        <label className="flex items-center gap-1 text-xs text-stone-300" title="Makes the check as the spell is cast, in place of an attack of opportunity.">
          <input type="checkbox" data-testid="cast-defensively" checked={castDefensively} onChange={(e) => setCastDefensively(e.target.checked)} />
          {systemRules.castDefensive}
        </label>
      )}
      {choosing && systemRules.metamagic && (
        <MetamagicPicker key={choosing.key} spell={choosing.spell} rules={systemRules.metamagic} known={metaKnown} campaignId={campaignId} tokenId={o.id} sceneId={scene?.id ?? null}
          onCast={(m) => pick(choosing, m)} onClose={() => setChoosing(null)} />
      )}
      {offPreset.length > 0 && (
        <p className={`text-stone-400 ${phoneLayout && !barOpen ? 'hidden' : ''}`} data-testid="hot-off-preset">
          {[...new Set(offPreset.map((e) => e.label))].join(', ')} {offPreset.length === 1 ? "isn't" : "aren't"} on {activeName}.{' '}
          <button type="button" className="underline" data-testid="hot-off-preset-add" onClick={addToPreset}>Add to this bar</button>{' '}
          <button type="button" className="underline" data-testid="hot-off-preset-leave" onClick={leaveOff}>Leave off</button>
        </p>
      )}
      {emptyWhy && (
        <p className="text-amber-200" role="status" data-testid="action-bar-why">
          {emptyWhy.text}
          {emptyWhy.sheet && <> <a className="underline" href={characterPageUrl(emptyWhy.sheet)} target={sheetWindowName(campaignId)} rel="noreferrer">Open my sheet</a></>}
        </p>
      )}
      <PendingAttack attackerId={o.id} />
      {assign !== null && (
        <BoxPicker key={assign} index={assign} box={assigned} entries={entries} asking={answer === 'asking' || checks === 'asking'} whyEmpty={tabWhy} defaultKey={defaultBoxKey(assign, look.past10)}
          onSave={(box) => {
            const from = box?.ref.startsWith(SHEET_SPELL) ? sheetRows.find((r) => r.row === box.ref.slice(SHEET_SPELL.length)) : null;
            if (!from) { const next = [...boxes]; next[assign] = box; saveBoxes(next); setAssign(null); return; }
            // A sheet spell goes on the token first; one whose entry leaves something open goes to New spell to finish.
            const spell = spellOfRow(from, systemRules);
            setAssign(null);
            if (!spell) { setFinishing({ row: from, box: assign }); setWriting(true); setSettings(false); return; }
            act({ kind: 'object.spells', id: o.id, spells: [...spells, spell] }, (reply) => {
              if (!reply.ok) { setNotice(reply.reason ?? 'That spell could not be saved.'); return; }
              const next = [...boxes]; next[assign] = { ...box!, ref: `spell:${spell.key}` }; saveBoxes(next);
            });
          }} onClose={() => setAssign(null)}
          onMove={(d) => { const to = assign + d; if (to < 0 || to >= boxes.length) return; move(assign, to); setAssign(to); }} first={assign === 0} last={assign === boxes.length - 1} />
      )}
      {settings && <Settings look={look} onClose={() => setSettings(false)} />}
      {spells.some((s) => (s.pending ?? 0) > 0) && (
        <PendingCasts spells={spells} mayAccept={role === 'player' && o.mine === true}
          onSettle={(key, accept) => act({ kind: 'object.spells', id: o.id, spells: spells.map((s) => { if (s.key !== key) return s; const { pending: _p, ...rest } = s; return rest; }), ...(accept ? { accept: [key] } : {}) } as never, (reply) => {
            if (!reply.ok) setNotice(reply.reason ?? 'That could not be saved.'); else if (reply.notice) setNotice(reply.notice);
          })} />
      )}
      {augmentAsk && (
        <div className="flex flex-wrap items-center gap-1 text-xs text-stone-300" role="group" aria-label={`Manifest ${augmentAsk.item.label}`} data-testid="augment-pick">
          <span>{`${augmentAsk.item.label}: ${augmentAsk.base} power ${augmentAsk.base === 1 ? 'point' : 'points'}, augment`}</span>
          <input type="number" aria-label="Augment points" data-testid="augment-points" min={0} max={augmentAsk.room} className={`${field} w-14`} value={augmentAsk.n}
            onChange={(e) => setAugmentAsk({ ...augmentAsk, n: Math.max(0, Math.min(augmentAsk.room, Math.trunc(Number(e.target.value) || 0))) })} />
          <span>{`of ${augmentAsk.room} (manifester level ${augmentAsk.row.points.ml}; ${augmentAsk.row.points.left} left)`}</span>
          <button type="button" className={btn} data-testid="augment-go" onClick={() => pick(augmentAsk.item, augmentAsk.meta, { level: augmentAsk.row.points.ml, attack: null, augment: augmentAsk.n })}>Manifest</button>
          <button type="button" className={btn} onClick={() => setAugmentAsk(null)}>Cancel</button>
        </div>
      )}
      {slotAsk && (
        <div className="flex flex-wrap items-center gap-1 text-xs text-stone-300" role="group" aria-label={`Cast ${slotAsk.item.label}`} data-testid="slot-pick">
          <span>{slotAsk.item.label}:</span>
          {slotAsk.choices.map((s) => <button type="button" key={s.which} className={btn} data-testid={`slot-${s.which}`} onClick={() => pick(slotAsk.item, slotAsk.meta, { ...slotAsk.how, slot: s.which, slotLevel: s.level })}>{`${slotWords(s.which)} (${s.left} left)`}</button>)}
          {slotAsk.item.spell.ritual && <button type="button" className={btn} data-testid="slot-ritual" onClick={() => pick(slotAsk.item, slotAsk.meta, { ...slotAsk.how, ritual: true })}>As a ritual</button>}
          <button type="button" className={btn} onClick={() => setSlotAsk(null)}>Cancel</button>
        </div>
      )}
      {writing && <SpellForm key={finishing?.row.row ?? 'new'} start={finishing?.row ?? null} typed={isDm} spells={spells} sheet={o.character ? { campaignId, tokenId: o.id, sceneId: scene?.id ?? null } : null} onSave={(next) => act({ kind: 'object.spells', id: o.id, spells: next }, (reply) => {
        if (!reply.ok) { setNotice(reply.reason ?? 'That spell could not be saved.'); return; }
        setWriting(false);
        if (finishing) { const put = [...boxes]; put[finishing.box] = { ref: `spell:${next[next.length - 1]!.key}` }; saveBoxes(put); setFinishing(null); }
      })} onClose={() => { setWriting(false); setFinishing(null); }} />}
    </section>
  );
}

/** "+ Preset": its name; it becomes the active preset, empty, ready to fill. */
function PresetName({ onSave, onClose }: { onSave: (n: string) => void; onClose: () => void }) {
  const [n, setN] = useState('');
  const t = n.trim();
  return (
    <form className="flex items-center gap-1" aria-label="New preset" onSubmit={(e) => { e.preventDefault(); if (t) onSave(t.slice(0, SPELL_LIMITS.loadoutName)); }}>
      <input aria-label="Preset name" placeholder="Boss fight" autoFocus maxLength={SPELL_LIMITS.loadoutName} className={`${field} w-28`} value={n} onChange={(e) => setN(e.target.value)} onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }} />
      <button type="submit" className={`${btn} border-amber-600`} disabled={!t}>Add</button>
      <button type="button" className={btn} onClick={onClose}>Cancel</button>
    </form>
  );
}

/** A hotkey typed by pressing it: the field reads the key press, Backspace clears it to the default. */
function HotkeyInput({ label, value, onSave }: { label: string; value: string; onSave: (k: string | null) => void }) {
  return (
    <label className="flex items-center gap-1">Key
      <input aria-label={label} readOnly className={`${field} w-24`} value={value} placeholder="none"
        onKeyDown={(e) => {
          if (e.key === 'Backspace' || e.key === 'Delete') { e.preventDefault(); onSave(null); return; }
          if (e.key === 'Escape' || (e.key === 'Tab' && !e.ctrlKey)) return;
          const k = keyName(e.nativeEvent);
          if (k && isHotkey(k)) { e.preventDefault(); onSave(k); }
        }} />
    </label>
  );
}

/** "+": what a box holds, picked from the sheet or stat block by tab and search, or a typed throw; and its label and key. */
function BoxPicker({ index, box, entries, asking, whyEmpty, defaultKey, onSave, onClose, onMove, first, last }: {
  index: number; box: HotBox | null; entries: HotEntry[]; asking: boolean; whyEmpty: (tab: HotTab) => string | null; defaultKey: string | null;
  onSave: (box: HotBox | null) => void; onClose: () => void;
  /** Move the box a place left or right (slice B): the touch way to reorder, where a finger cannot drag. */
  onMove: (d: -1 | 1) => void; first: boolean; last: boolean;
}) {
  const [icon, setIcon] = useState<string | null>(box?.icon ?? null);
  const [picture, setPicture] = useState<string | null>(box?.picture ?? null);
  const [uploading, setUploading] = useState<string | null>(null);
  const upload = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > PICTURE_LIMITS.bytes) { setUploading(`That picture is too big: the limit is ${Math.round(PICTURE_LIMITS.bytes / 1048576)} MB.`); return; }
    setUploading('Uploading…');
    const got = await uploadBoxPicture((file.name || 'Box picture').slice(0, PICTURE_LIMITS.name), file);
    if (got.kind === 'ok') { setPicture(got.asset.id); setUploading(got.warning); return; }
    setUploading(got.kind === 'refused' ? got.reason : 'That picture could not be uploaded just now.');
  };
  const startTab = (box?.ref.split(':')[0] as HotTab | undefined) ?? (entries[0]?.tab ?? 'attack');
  const [tab, setTab] = useState<HotTab>(TABS.some((t) => t.tab === startTab) ? startTab : 'attack');
  const [find, setFind] = useState('');
  const [ref, setRef] = useState<string | null>(box?.ref ?? null);
  const [label, setLabel] = useState(box?.label ?? '');
  const [hotkey, setHotkey] = useState<string | null>(box?.hotkey ?? null);
  const [dice, setDice] = useState(box?.ref.startsWith('dice:') ? box.ref.slice(5) : '');
  const q = find.trim().toLowerCase();
  const list = entries.filter((e) => e.tab === tab && (!q || e.label.toLowerCase().includes(q)));
  const typed = tab === 'dice' ? diceRef(dice) : null;
  const chosen = tab === 'dice' ? typed : ref;
  const meta = TABS.find((t) => t.tab === tab)!;
  const save = (r: string | null) => {
    if (!r) return;
    const l = label.trim().slice(0, HOT_LIMITS.label);
    onSave({ ref: r, ...(l ? { label: l } : {}), ...(hotkey ? { hotkey } : {}), ...(icon && isHotIcon(icon) ? { icon } : {}), ...(picture ? { picture } : {}) });
  };
  return (
    <div className="flex flex-col gap-1 rounded border border-amber-800 bg-stone-950 p-2" role="dialog" aria-label={`Fill box ${index + 1}`} data-testid="hot-picker"
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}>
      <div role="tablist" aria-label="What the box holds" className="flex flex-wrap gap-1">
        {TABS.map((t) => {
          const n = t.tab === 'dice' ? null : entries.filter((e) => e.tab === t.tab).length;
          return <button key={t.tab} type="button" role="tab" aria-selected={tab === t.tab} className={`${btn} ${tab === t.tab ? 'border-amber-500 text-amber-300' : ''}`} onClick={() => { setTab(t.tab); setFind(''); }}>{t.label}{n !== null ? ` (${n})` : ''}</button>;
        })}
      </div>
      {tab === 'dice' ? (
        <label className="flex items-center gap-1">Dice
          <input aria-label="Dice to roll" placeholder="2d6+3" className={`${field} w-24`} value={dice} onChange={(e) => setDice(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && typed) save(typed); }} />
          {dice.trim() && !typed && <span className="text-amber-300">Dice read like d20, 2d6+3 or 1d8-1.</span>}
        </label>
      ) : (
        <>
          <input aria-label={meta.find} placeholder={meta.find} autoFocus className={`${field} w-full`} value={find} onChange={(e) => setFind(e.target.value)} />
          <ul className="flex max-h-40 flex-col gap-0.5 overflow-y-auto" aria-label={meta.label}>
            {list.map((e) => (
              <li key={e.ref}>
                <button type="button" aria-pressed={ref === e.ref} className={`${btn} flex w-full justify-between gap-2 ${ref === e.ref ? 'border-amber-500 text-amber-300' : ''}`} onClick={() => setRef(e.ref)} onDoubleClick={() => save(e.ref)}>
                  <span>{e.label}</span><span className="text-stone-400">{e.detail}</span>
                </button>
              </li>
            ))}
            {list.length === 0 && <li className="text-stone-500">{asking ? 'Reading the sheet…' : q ? 'Nothing matches.' : whyEmpty(tab) ?? `No ${meta.label.toLowerCase()} to read from the sheet or stat block.`}</li>}
          </ul>
        </>
      )}
      <div className="flex flex-wrap items-center gap-1" role="radiogroup" aria-label="Box picture" data-testid="hot-icons">
        <span className="text-stone-400">Picture</span>
        <button type="button" role="radio" aria-checked={!icon && !picture} className={`${btn} ${!icon && !picture ? 'border-amber-500 text-amber-300' : ''}`} onClick={() => { setIcon(null); setPicture(null); }}>None</button>
        {HOT_ICON_LIST.map((n) => (
          <button key={n} type="button" role="radio" aria-checked={icon === n && !picture} aria-label={HOT_ICON_WORDS[n]} title={HOT_ICON_WORDS[n]}
            className={`${btn} px-1 ${icon === n && !picture ? 'border-amber-500 text-amber-300' : 'text-stone-300'}`} onClick={() => { setIcon(n); setPicture(null); }}>
            <HotIconSvg name={n} size={18} />
          </button>
        ))}
        <label className={`${btn} cursor-pointer ${picture ? 'border-amber-500 text-amber-300' : ''}`} title="A picture of your own: only you see it (and the DM, through this box)">
          {picture ? <span className="flex items-center gap-1"><BoxPicture id={picture} px={18} fallback="•" />Your picture</span> : 'Upload a picture…'}
          <input type="file" accept="image/png,image/jpeg,image/webp" aria-label="Upload a picture for the box" className="sr-only" onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
        {uploading && <span className="text-amber-300" role="status">{uploading}</span>}
      </div>
      <div className="flex flex-wrap items-center gap-1">
        <input aria-label="Box label" placeholder="Label (optional)" maxLength={HOT_LIMITS.label} className={`${field} w-32`} value={label} onChange={(e) => setLabel(e.target.value)} />
        <HotkeyInput label="Box key" value={hotkey ?? defaultKey ?? ''} onSave={setHotkey} />
        <button type="button" className={`${btn} border-amber-600`} disabled={!chosen} onClick={() => save(chosen)}>Put in box {index + 1}</button>
        {box && <button type="button" className={btn} onClick={() => onSave(null)}>Empty the box</button>}
        <button type="button" className={btn} disabled={first} aria-label={`Move box ${index + 1} left`} onClick={() => onMove(-1)}>◀ Move</button>
        <button type="button" className={btn} disabled={last} aria-label={`Move box ${index + 1} right`} onClick={() => onMove(1)}>Move ▶</button>
        <button type="button" className={btn} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

/** The ⚙: this person's look of the bar, kept in their layout document; Reset forgets it all. */
function Settings({ look, onClose }: { look: HotbarLook; onClose: () => void }) {
  const set = panelPrefs.setHotbar;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded border border-stone-700 bg-stone-950 p-2" role="dialog" aria-label="Hot bar settings" data-testid="hot-settings">
      <label className="flex items-center gap-1">Box size
        <input type="range" aria-label="Box size" min={HOTBAR_LIMITS.size[0]} max={HOTBAR_LIMITS.size[1]} value={look.size} onChange={(e) => set({ size: Number(e.target.value) })} />
        <span className="w-8 tabular-nums">{look.size}</span>
      </label>
      <label className="flex items-center gap-1">Opacity
        <input type="range" aria-label="Opacity" min={HOTBAR_LIMITS.opacity[0]} max={HOTBAR_LIMITS.opacity[1]} value={look.opacity} onChange={(e) => set({ opacity: Number(e.target.value) })} />
        <span className="w-8 tabular-nums">{look.opacity}%</span>
      </label>
      <label className="flex items-center gap-1">Boxes
        <select aria-label="Boxes" className={field} value={look.count === null ? 'fill' : 'set'} onChange={(e) => set({ count: e.target.value === 'fill' ? null : Math.max(HOTBAR_LIMITS.count[0], look.count ?? 10) })}>
          <option value="fill">Fill the bar</option><option value="set">A set number</option>
        </select>
        {look.count !== null && <input type="number" aria-label="Number of boxes" min={HOTBAR_LIMITS.count[0]} max={HOTBAR_LIMITS.count[1]} className={`${field} w-14`} value={look.count}
          onChange={(e) => { const n = Math.trunc(Number(e.target.value)); if (n >= HOTBAR_LIMITS.count[0] && n <= HOTBAR_LIMITS.count[1]) set({ count: n }); }} />}
      </label>
      <label className="flex items-center gap-1">Rows
        <select aria-label="Rows" className={field} value={look.rows} onChange={(e) => set({ rows: Number(e.target.value) as 1 | 2 | 3 })}>
          <option value={1}>1</option><option value={2}>2</option><option value={3}>3</option>
        </select>
      </label>
      <label className="flex items-center gap-1">Labels
        <select aria-label="Labels" className={field} value={look.labels} onChange={(e) => set({ labels: e.target.value as 'hover' | 'always' })}>
          <option value="hover">On hover</option><option value="always">Always</option>
        </select>
      </label>
      <label className="flex items-center gap-1">Position
        <select aria-label="Position" className={field} value={look.position} onChange={(e) => set({ position: e.target.value as 'top' | 'bottom' })}>
          <option value="bottom">Bottom of the board</option><option value="top">Top of the board</option>
        </select>
      </label>
      <label className="flex items-center gap-1" title="Chrome keeps Ctrl+1… for its tabs, so boxes 11–20 use Alt+1…, or no key">Boxes 11–20
        <select aria-label="Keys of boxes 11 to 20" className={field} value={look.past10} onChange={(e) => set({ past10: e.target.value as 'alt' | 'none' })}>
          <option value="alt">Alt+1…Alt+0</option><option value="none">No key</option>
        </select>
      </label>
      <HotkeyInput label="Next preset key" value={look.next} onSave={(k) => set({ next: k ?? 'Tab' })} />
      <HotkeyInput label="Previous preset key" value={look.prev} onSave={(k) => set({ prev: k ?? 'Shift+Tab' })} />
      <button type="button" className={btn} onClick={() => set(null)}>Reset</button>
      <button type="button" className={btn} onClick={onClose}>Close</button>
    </div>
  );
}

/** "New spell…": a template and its save, written to the token's spells. Sizes in the system's unit. */
/** What a success does, for a save line that leaves it open: the area form's own words. */
const SPELL_EFFECT_WORDS: Record<OnSave, string> = { half: 'half damage on a success', none: 'no damage on a success', full: 'full damage on a success' };

/**
 * The DM's casts with a player's character not yet on the player's sheet (qa-runs 1180; as hit points wait, #824): the
 * player accepts them onto the sheet or declines them; the DM sees them waiting.
 */
function PendingCasts({ spells, mayAccept, onSettle }: { spells: Spell[]; mayAccept: boolean; onSettle: (key: string, accept: boolean) => void }) {
  return (
    <ul className="flex flex-col gap-0.5 text-amber-200" data-testid="pending-casts">
      {spells.filter((s) => (s.pending ?? 0) > 0).map((s) => (
        <li key={s.key} className="flex flex-wrap items-center gap-1">
          {s.name}: {s.pending === 1 ? 'one cast' : `${s.pending} casts`} by the DM, not on {mayAccept ? 'your' : 'the'} sheet yet.
          {mayAccept && <button type="button" className={btn} onClick={() => onSettle(s.key, true)}>Accept</button>}
          {mayAccept && <button type="button" className={btn} onClick={() => onSettle(s.key, false)}>Decline</button>}
        </li>
      ))}
    </ul>
  );
}

/** A picked sheet row's ref, until it is a spell on the token. */
const SHEET_SPELL = 'sheet-spell:';
/** A sheet trait's key on the Abilities tab: read off, never sent to the server as a monster's ability is. */
const TRAIT = 'trait-';

/** A sheet row as a saved spell, when its entry settles everything the form would ask; else null, for the form. */
function spellOfRow(r: SheetSpellRow, rules: { distance?: { perSquare: number } | null; saves: Parameters<typeof parseSaveLine>[1]; actions?: { kinds: ReadonlyArray<{ key: string; label: string }> } | null }): Spell | null {
  const f = r.fill;
  if (!f) return null;
  // Words that aren't a save a table can roll ("See text", "No") are no save line; a real one needs the sheet's DC.
  const line = entrySaveLine(f, rules.saves) ?? '';
  if (line && r.dc === undefined) return null;
  // An area the entry doesn't state in squares is cast with no template; New spell can still draw one.
  const area = entryArea(f, rules.distance?.perSquare ?? null) ?? { noArea: true as const };
  const dice = f.damage && parseDamageText(f.damage) ? f.damage : '';
  const damage = dice && r.damageType && /^[\d\sd+-]+$/i.test(dice) ? `${dice} ${r.damageType}` : dice;
  const kinds = rules.actions?.kinds ?? [];
  const time = f.time ? castingKindOf(f.time, kinds) : null;
  return {
    key: `spell-${crypto.randomUUID().slice(0, 8)}`, name: r.name.slice(0, SPELL_LIMITS.name),
    ...('noArea' in area ? { shape: 'burst' as const, size: 1, noArea: true as const } : area),
    saveLine: line, dc: line ? r.dc! : null, ...(damage ? { damage } : {}), ...(kinds.length ? { castingTime: time ?? (kinds.some((k) => k.key === 'standard') ? 'standard' : kinds[0]!.key) } : {}), row: r.row, ...castKeysOf(r),
  };
}

/** A spell the sheet has ready today, which fills a box by itself: a prepared caster's book holds more than the day's spells. */
const isReadyToday = (r: SheetSpellRow): boolean => r.prepared === undefined || r.prepared > 0 || r.spontaneous === true || r.atWill;

/** A spell's numbers as a person reads them: its template in feet, save, DC and damage. */
function spellWords(s: Spell, perSquare: number | null): string {
  const area = !s.noArea && perSquare ? `${s.size * perSquare} ft. ${SHAPE_WORDS[s.shape].toLowerCase()}` : '';
  return [area, s.saveLine, s.dc !== null ? `DC ${s.dc}` : '', s.damage ?? ''].filter(Boolean).join(' · ');
}

function SpellForm({ start, typed, spells, sheet, onSave: onSaveSpells, onClose }: { start?: SheetSpellRow | null; typed: boolean; spells: Spell[]; sheet: { campaignId: string; tokenId: string; sceneId: string | null } | null; onSave: (next: Spell[]) => void; onClose: () => void }) {
  const { systemRules } = useTableState();
  const dist = systemRules.distance;
  const [name, setName] = useState(start?.name.slice(0, SPELL_LIMITS.name) ?? '');
  // A spell read from the sheet (qa-runs 1180): its row's id rides on the saved spell, so a cast counts on the sheet.
  const [row, setRow] = useState(start?.row ?? '');
  const [fromSheet, setFromSheet] = useState<SheetSpellsAnswer | 'asking'>(sheet ? 'asking' : null);
  useEffect(() => {
    if (!sheet) return undefined;
    let live = true;
    void fetchSheetSpells(sheet.campaignId, sheet.tokenId, sheet.sceneId).then((a) => { if (live) setFromSheet(a); });
    return () => { live = false; };
  }, [sheet?.campaignId, sheet?.tokenId, sheet?.sceneId]);
  const sheetRows = fromSheet && fromSheet !== 'asking' ? fromSheet.rows : [];
  const sheetRow = sheetRows.find((r) => r.row === row) ?? (start?.row === row ? start : undefined);
  const [held, setHeld] = useState(false);
  // No area (qa-runs 1180): a touch or single-target spell, cast without a template; the only kind without distance rules.
  const [shape, setShape] = useState<AreaShape | 'none'>(dist ? 'burst' : 'none');
  const [size, setSize] = useState('');
  const [size2, setSize2] = useState('');
  const [line, setLine] = useState('');
  const [onSave, setOnSave] = useState<OnSave>('half');
  const [dc, setDc] = useState('');
  const [damage, setDamage] = useState('');
  const damageTypes = (systemRules.damage?.types ?? []).filter((t) => !t.physical);
  const [damageType, setDamageType] = useState('');
  /** The damage as it is kept: with the chosen type, unless the words already say one. */
  const damageWords = damageType && /^[\d\sd+-]+$/i.test(damage.trim()) ? `${damage.trim()} ${damageType}` : damage.trim();
  // The action its casting time names (qa-runs 408), from the system's own kinds: a standard action unless changed.
  const kinds = systemRules.actions?.kinds ?? [];
  const [time, setTime] = useState<string>(() => (kinds.some((k) => k.key === 'standard') ? 'standard' : kinds[0]?.key ?? LONG_CAST));
  // What the spell's entry holds: the rest stays as it is for the player to give.
  const fillFrom = (r: SheetSpellRow) => {
    const f = r.fill;
    if (!f) return;
    if (f.shape && dist) setShape(f.shape);
    setSize(f.size ? String(f.size) : '');
    setSize2(f.shape === 'rect' && f.size ? String(f.size) : '');
    setLine((f.save ?? '').slice(0, SPELL_LIMITS.saveLine));
    setDc(f.save && r.dc !== undefined ? String(r.dc) : '');
    setDamage(f.damage ?? '');
    const t = f.time ? castingKindOf(f.time, kinds) : null;
    if (t) setTime(t);
  };
  useEffect(() => { if (start) fillFrom(start); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const none = shape === 'none' || !dist;
  const squares = (v: string) => { if (!dist) return null; const n = Number(v) / dist.perSquare; return Number.isFinite(n) && n >= 1 && n <= AREA_LIMITS.squares ? Math.round(n) : null; };
  const sq = squares(size); const sq2 = shape === 'rect' ? squares(size2 || size) : null;
  const dcN = Math.trunc(Number(dc));
  const dcOk = !line.trim() || (dc.trim() !== '' && dcN >= 1 && dcN <= SAVE_LIMITS.dc);
  const damageOk = !damage.trim() || parseDamageText(damage) !== null;
  const reads = line.trim() ? parseSaveLine(line, systemRules.saves) : null;
  const ready = name.trim().length >= 1 && (none || sq !== null) && (shape !== 'rect' || sq2 !== null) && dcOk && damageOk && spells.length < SPELL_LIMITS.spells;
  // Why Save spell is off (qa-runs 1259), the first thing left to do. A bad damage line says its own words below.
  let notReady = '';
  if (!ready) {
    if (spells.length >= SPELL_LIMITS.spells) notReady = 'This token holds as many spells as it can.';
    else if (!name.trim()) notReady = 'Give it a name to save it.';
    else if (!none && (sq === null || (shape === 'rect' && sq2 === null))) notReady = 'Give it a size to save it, or choose No area.';
    else if (!dcOk) notReady = 'A save line needs a DC.';
  }
  return (
    <form className="flex flex-wrap items-center gap-1 rounded border border-sky-800 p-2" aria-label="New spell" onSubmit={(e) => {
      e.preventDefault();
      if (!ready) return;
      const spell: Spell = {
        key: `spell-${crypto.randomUUID().slice(0, 8)}`, name: name.trim().slice(0, SPELL_LIMITS.name),
        ...(none ? { shape: 'burst' as const, size: 1, noArea: true as const } : { shape: shape as AreaShape, size: sq! }),
        ...(!none && shape === 'rect' ? { size2: sq2! } : {}), saveLine: line.trim(), dc: line.trim() ? dcN : null,
        ...(damageWords ? { damage: damageWords } : {}),
        ...(reads?.leftToDm ? { onSave } : {}),
        ...(kinds.length ? { castingTime: time } : {}),
        ...(row ? { row } : {}),
        ...(sheetRow ? castKeysOf(sheetRow) : {}),
        ...(held ? { concentration: true as const } : {}),
      };
      onSaveSpells([...spells, spell]);
    }}>
      {sheetRows.length > 0 && (
        <select aria-label="Spell from your sheet" className={field} value={row} onChange={(e) => {
          const r = sheetRows.find((x) => x.row === e.target.value);
          setRow(r ? r.row : '');
          if (r) { setName(r.name.slice(0, SPELL_LIMITS.name)); setDamageType(r.damageType ?? ''); fillFrom(r); }
        }}>
          {typed && <option value="">Not from the sheet</option>}
          {sheetRows.map((r) => <option key={r.row} value={r.row}>{r.name}{r.atWill ? ' (at will)' : ` (${r.cast} cast today)`}</option>)}
        </select>
      )}
      {fromSheet && fromSheet !== 'asking' && fromSheet.notice && <span className="text-amber-300">{fromSheet.notice}</span>}
      <input aria-label="Spell name" placeholder="e.g. Fireball" maxLength={SPELL_LIMITS.name} className={`${field} w-28`} readOnly={!typed} value={name} onChange={(e) => setName(e.target.value)} />
      <select aria-label="Spell shape" className={field} value={none ? 'none' : shape} onChange={(e) => setShape(e.target.value as AreaShape | 'none')}>
        {dist && AREA_SHAPES.map((s) => <option key={s} value={s}>{SHAPE_WORDS[s]}</option>)}
        <option value="none">No area (touch, one target)</option>
      </select>
      {!none && dist && (
        <label className="flex items-center gap-1">{shape === 'burst' ? 'Radius' : shape === 'rect' ? 'Width' : 'Length'}
          <input aria-label={`Spell size in ${dist.unit}`} type="number" step={dist.perSquare} min={dist.perSquare} className={`${field} w-14`} value={size} onChange={(e) => setSize(e.target.value)} /> {dist.unit}
        </label>
      )}
      {!none && dist && shape === 'rect' && (
        <label className="flex items-center gap-1">Height
          <input aria-label={`Spell height in ${dist.unit}`} type="number" step={dist.perSquare} min={dist.perSquare} className={`${field} w-14`} value={size2} onChange={(e) => setSize2(e.target.value)} /> {dist.unit}
        </label>
      )}
      <input aria-label="Spell save line" placeholder={systemRules.saves[0] ? `e.g. ${systemRules.saves[0].label} half` : 'Save line'} maxLength={SPELL_LIMITS.saveLine} className={`${field} w-28`} value={line} onChange={(e) => setLine(e.target.value)} />
      <label className="flex items-center gap-1">DC <input aria-label="Spell DC" type="number" min={1} max={SAVE_LIMITS.dc} className={`${field} w-12`} value={dc} onChange={(e) => setDc(e.target.value)} /></label>
      <input aria-label="Spell damage" placeholder="e.g. 6d6" maxLength={SPELL_LIMITS.damage} className={`${field} w-20`} value={damage} onChange={(e) => setDamage(e.target.value)} />
      {damageTypes.length > 0 && (
        <select aria-label="Spell damage type" className={field} value={damageType} onChange={(e) => setDamageType(e.target.value)}>
          <option value="">No type</option>
          {damageTypes.map((t) => <option key={t.key} value={t.label.toLowerCase()}>{t.label}</option>)}
        </select>
      )}
      {reads?.leftToDm && (
        <select aria-label="Spell on a save" className={field} value={onSave} onChange={(e) => setOnSave(e.target.value as OnSave)}>
          {ON_SAVE.map((o) => <option key={o} value={o}>{SPELL_EFFECT_WORDS[o]}</option>)}
        </select>
      )}
      {systemRules.concentrates && <label className="flex items-center gap-1"><input type="checkbox" data-testid="spell-concentration" checked={held || !!sheetRow?.fill?.concentration} onChange={(e) => setHeld(e.target.checked)} />Concentration</label>}
      {kinds.length > 0 && (
        <label className="flex items-center gap-1">Casting time
          <select aria-label="Spell casting time" className={field} value={time} onChange={(e) => setTime(e.target.value)}>
            {kinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
            <option value={LONG_CAST}>Longer than a round</option>
          </select>
        </label>
      )}
      {line.trim() && !reads && !none && <span className="text-amber-300">That line names none of this system's saves: the spell will only lay its template.</span>}
      {!damageOk && <span className="text-amber-300">Damage reads like 6d6, or 2d6 + 3.</span>}
      {notReady && <span className="text-amber-300" data-testid="spell-not-ready">{notReady}</span>}
      <button type="submit" className={`${btn} border-sky-600`} disabled={!ready} title={notReady || undefined}>Save spell</button>
      <button type="button" className={btn} onClick={onClose}>Close</button>
    </form>
  );
}

/** A box's face (slice B): its own uploaded picture, else its generic picture, else its name's initials. */
function BoxFace({ box, short, px }: { box: HotBox; short: string; px: number }) {
  const side = Math.max(10, Math.round(px * 0.62));
  if (box.picture) return <BoxPicture id={box.picture} px={px} fallback={short} />;
  if (box.icon && isHotIcon(box.icon)) return <span aria-hidden="true" className="flex items-center justify-center"><HotIconSvg name={box.icon} size={side} /></span>;
  return <span aria-hidden="true" className="font-semibold">{short}</span>;
}

/**
 * A picture the player uploaded for a box, fetched with the bearer (never a public URL) and drawn from this tab's own
 * object URL. Until it arrives, or when it cannot be read, the box shows its fallback.
 */
function BoxPicture({ id, px, fallback }: { id: string; px: number; fallback: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setUrl(null);
    void pictureUrl(id).then((u) => { if (live) setUrl(u); });
    return () => { live = false; };
  }, [id]);
  if (!url) return <span aria-hidden="true" className="font-semibold">{fallback}</span>;
  return <img src={url} alt="" aria-hidden="true" draggable={false} data-testid="hot-box-picture" className="pointer-events-none h-full w-full object-cover" style={{ maxWidth: px, maxHeight: px }} />;
}
